/**
 * DcfComponentBase — shared base class for DCF and Reverse DCF components.
 *
 * Contains all computed fields, projection logic, helpers, and UI update methods
 * that are identical between dcf.js and reversedcf.js.
 *
 * Subclasses override:
 *   - _computeWacc()            — RDCF adds solveVariable === 'wacc' branch
 *   - _calcSensitivityCell()    — DCF returns implied price, RDCF returns implied solve value
 *   - _getExitMetricValue()     — can be overridden for custom exit metric logic
 *   - _buildMetaSummaryParts()  — RDCF adds "Solve For" part
 */
export class DcfComponentBase extends Tango.Component {

	// ══════════════════════════════════════════════
	//  WACC Computed Fields
	// ══════════════════════════════════════════════

	_defineWaccComputed() {
		this.defineComputed('costOfEquity', () => {
			const s = this.state;
			return s.riskFreeRate
				+ s.betaLevered * s.equityRiskPremium
				+ s.sizePremium
				+ s.countryRiskPremium
				+ s.specificRiskPremium;
		});

		this.defineComputed('afterTaxCostOfDebt', () => {
			return this.state.preTaxCostOfDebt * (1 - this.state.effectiveTaxRate);
		});

		this.defineComputed('marketCapInReportCurrency', () => {
			const fx = this.state.fxRate;
			return fx !== 0 ? this.state.marketCap / fx : this.state.marketCap;
		});

		this.defineComputed('equityWeight', () => {
			const mcReport = this.state.marketCapInReportCurrency;
			const total = mcReport + this.state.totalDebt;
			return total !== 0 ? mcReport / total : 0;
		});

		this.defineComputed('debtWeight', () => {
			const mcReport = this.state.marketCapInReportCurrency;
			const total = mcReport + this.state.totalDebt;
			return total !== 0 ? this.state.totalDebt / total : 0;
		});

		this.defineComputed('wacc', () => this._computeWacc());
	}

	/** Subclass may override (e.g. RDCF handles solveVariable === 'wacc') */
	_computeWacc() {
		const s = this.state;
		if (s.dcfApproach === 'fcfe') {
			return this.state.costOfEquity;
		}
		return this.state.costOfEquity * this.state.equityWeight
			+ this.state.afterTaxCostOfDebt * this.state.debtWeight;
	}

	// ══════════════════════════════════════════════
	//  Projection Computed Fields (per period)
	// ══════════════════════════════════════════════

	_defineProjectionComputed() {
		const periods = this.state.periods;
		if (!periods || periods.length === 0) return;

		for (let i = 0; i < periods.length; i++) {
			const p = periods[i];
			if (p.isActual) continue;

			const prevIdx = i > 0 ? i - 1 : null;

			// Revenue = prev.Revenue × (1 + RevenueGrowth)
			this.defineComputed(
				st => st.periods[i],
				'revenue',
				o => {
					if (prevIdx === null) return null;
					const prev = this.state.periods[prevIdx];
					if (prev.revenue == null || o.revenueGrowth == null) return null;
					return prev.revenue * (1 + o.revenueGrowth);
				}
			);

			// EBITDA = Revenue × EbitdaMargin
			this.defineComputed(
				st => st.periods[i],
				'ebitda',
				o => {
					if (o.revenue == null || o.ebitdaMargin == null) return null;
					return o.revenue * o.ebitdaMargin;
				}
			);

			// ── Absolute FCF drivers from per-period ratios ──

			// D&A = Revenue × DaRatio
			this.defineComputed(
				st => st.periods[i],
				'depreciationAmortization',
				o => {
					if (o.revenue == null || o.daRatio == null) return null;
					return o.revenue * o.daRatio;
				}
			);

			// Capex = Revenue × CapexRatio
			this.defineComputed(
				st => st.periods[i],
				'capex',
				o => {
					if (o.revenue == null || o.capexRatio == null) return null;
					return o.revenue * o.capexRatio;
				}
			);

			// ΔWC = Revenue × NwcRatio
			this.defineComputed(
				st => st.periods[i],
				'changeInNwc',
				o => {
					if (o.revenue == null || o.nwcRatio == null) return null;
					return o.revenue * o.nwcRatio;
				}
			);

			// SBC = Revenue × SbcRatio
			this.defineComputed(
				st => st.periods[i],
				'stockBasedCompensation',
				o => {
					if (o.revenue == null || o.sbcRatio == null) return null;
					return o.revenue * o.sbcRatio;
				}
			);

			// EBIT = EBITDA − D&A
			this.defineComputed(
				st => st.periods[i],
				'ebit',
				o => {
					if (o.ebitda == null) return null;
					return o.ebitda - (o.depreciationAmortization || 0);
				}
			);

			// EBIT Margin = EBIT / Revenue
			this.defineComputed(
				st => st.periods[i],
				'ebitMargin',
				o => {
					if (o.revenue == null || o.revenue === 0 || o.ebit == null) return null;
					return o.ebit / o.revenue;
				}
			);

			// NOPAT = EBIT × (1 − TaxRate)
			this.defineComputed(
				st => st.periods[i],
				'nopat',
				o => {
					if (o.ebit == null || o.taxRate == null) return null;
					return o.ebit * (1 - o.taxRate);
				}
			);

			// Unlevered FCF = NOPAT + D&A − Capex − ΔWC + SBC + Other
			this.defineComputed(
				st => st.periods[i],
				'unleveredFcf',
				o => {
					if (o.nopat == null) return null;
					return o.nopat
						+ (o.depreciationAmortization || 0)
						- (o.capex || 0)
						- (o.changeInNwc || 0)
						+ (o.stockBasedCompensation || 0)
						+ (o.otherAdjustments || 0);
				}
			);

			// Discount Period
			this.defineComputed(
				st => st.periods[i],
				'discountPeriod',
				o => {
					let forecastIndex = 0;
					for (let j = 0; j < i; j++) {
						if (!this.state.periods[j].isActual) forecastIndex++;
					}
					return this.state.midYearConvention ? forecastIndex + 0.5 : forecastIndex + 1;
				}
			);

			// Discount Factor = 1 / (1 + WACC) ^ period
			this.defineComputed(
				st => st.periods[i],
				'discountFactor',
				o => {
					if (o.discountPeriod == null) return null;
					return Math.pow(1 + this.state.wacc, -o.discountPeriod);
				}
			);

			// PV of FCF = UFCF × DiscountFactor
			this.defineComputed(
				st => st.periods[i],
				'pvFcf',
				o => {
					if (o.unleveredFcf == null || o.discountFactor == null) return null;
					return o.unleveredFcf * o.discountFactor;
				}
			);
		}
	}

	// ══════════════════════════════════════════════
	//  Aggregate: Sum of PV of FCFs
	// ══════════════════════════════════════════════

	_defineSumPvFcf() {
		this.defineComputed('sumPvFcf', () => {
			let sum = 0;
			for (const p of this.state.periods) {
				if (!p.isActual && p.pvFcf != null) sum += p.pvFcf;
			}
			return sum;
		});
	}

	// ══════════════════════════════════════════════
	//  Terminal Value Computed Fields
	// ══════════════════════════════════════════════

	_defineTvComputed() {
		this.defineComputed('terminalFcf', () => {
			const last = this._lastForecastPeriod();
			if (!last || last.unleveredFcf == null) return 0;
			return last.unleveredFcf * (1 + this.state.terminalGrowthRate);
		});

		this.defineComputed('tvPerpetuityUndiscounted', () => {
			const wacc = this.state.wacc;
			const g = this.state.terminalGrowthRate;
			const denom = wacc - g;
			if (denom <= 0) return 0;
			return this.state.terminalFcf / denom;
		});

		this.defineComputed('exitMetricValue', () => {
			return this._getExitMetricValue();
		});

		this.defineComputed('tvExitUndiscounted', () => {
			return this.state.exitMetricValue * this.state.exitMultiple;
		});

		this.defineComputed('tvBlendedUndiscounted', () => {
			const s = this.state;
			return this._blendTv(s, s.tvPerpetuityUndiscounted, s.tvExitUndiscounted);
		});

		this.defineComputed('tvDiscountFactor', () => {
			const last = this._lastForecastPeriod();
			if (!last || last.discountPeriod == null) return 0;
			const wacc = this.state.wacc;
			const period = this.state.midYearConvention
				? last.discountPeriod + 0.5
				: last.discountPeriod;
			return Math.pow(1 + wacc, -period);
		});

		this.defineComputed('pvTerminalValue', () => {
			return this.state.tvBlendedUndiscounted * this.state.tvDiscountFactor;
		});

		this.defineComputed('enterpriseValue', () => {
			return this.state.sumPvFcf + this.state.pvTerminalValue;
		});

		this.defineComputed('tvAsPercentOfEv', () => {
			const ev = this.state.enterpriseValue;
			return ev !== 0 ? (this.state.pvTerminalValue / ev) * 100 : 0;
		});
	}

	/** Override for custom exit metric selection. Default: EBITDA, with fallback switch. */
	_getExitMetricValue() {
		const last = this._lastForecastPeriod();
		if (!last) return 0;
		switch (this.state.exitMetric) {
			case 'EV/EBITDA': return last.ebitda || 0;
			case 'EV/EBIT': return last.ebit || 0;
			case 'EV/Revenue': return last.revenue || 0;
			case 'EV/FCF': return last.unleveredFcf || 0;
			default: return last.ebitda || 0;
		}
	}

	// ══════════════════════════════════════════════
	//  Projection Subtitle
	// ══════════════════════════════════════════════

	_defineProjectionSubtitle() {
		this.defineComputed('projectionSubtitleSuffix', () => {
			if (this._isCrossCurrency()) {
				return `, except per share data (in ${this.state.priceCurrency})`;
			}
			return ', except per share data';
		});
	}

	// ══════════════════════════════════════════════
	//  Sensitivity Computed Fields
	// ══════════════════════════════════════════════

	_defineSensitivityComputed() {
		const tables = this.state.sensitivityTables;
		if (!tables) return;

		for (let t = 0; t < tables.length; t++) {
			this.defineComputed(
				st => st.sensitivityTables[t],
				'baseRowIndex',
				o => this._findClosestIndex(o.rowValues, this.state.wacc)
			);

			this.defineComputed(
				st => st.sensitivityTables[t],
				'baseColIndex',
				o => this._findClosestIndex(o.colValues, this._getSensitivityBaseColValue(o))
			);

			this.defineComputed(
				st => st.sensitivityTables[t],
				'matrix',
				o => {
					const matrix = [];
					for (let r = 0; r < o.rowValues.length; r++) {
						matrix[r] = [];
						for (let c = 0; c < o.colValues.length; c++) {
							matrix[r][c] = this._calcSensitivityCell(o, o.rowValues[r], o.colValues[c]);
						}
					}
					return matrix;
				}
			);
		}
	}

	/** Must be implemented by subclass */
	_calcSensitivityCell(table, rowVal, colVal) {
		return 0;
	}

	// ══════════════════════════════════════════════
	//  Shared UI Update Methods
	// ══════════════════════════════════════════════

	/** Hide projection rows where all values are zero/null */
	_updateHideIfZeroRows() {
		const root = document.getElementById(this.root);
		if (!root) return;

		root.querySelectorAll('.row-hide-if-zero').forEach(row => {
			const field = row.dataset.field;
			if (!field) return;
			const allZero = this.state.periods.every(p => !p[field] || p[field] === 0);
			row.style.display = allZero ? 'none' : '';
		});
	}

	/** Update TV as % of EV color indicator */
	_updateTvPercentIndicator() {
		const root = document.getElementById(this.root);
		if (!root) return;
		const el = root.querySelector('.tv-metric span:last-child');
		if (!el) return;

		const pct = this.state.tvAsPercentOfEv;
		el.classList.remove('indicator-ok', 'indicator-warning');
		el.classList.add(pct >= 60 && pct <= 80 ? 'indicator-ok' : 'indicator-warning');
	}

	/** Toggle visibility of cross-currency elements */
	_updateCrossCurrencyVisibility() {
		const root = document.getElementById(this.root);
		if (!root) return;
		const isCross = this._isCrossCurrency();

		root.querySelectorAll('.dcf-fx-field').forEach(el => {
			el.style.display = isCross ? '' : 'none';
		});
	}

	/** Toggle TV blend weight fields based on method */
	_updateBlendWeightVisibility() {
		const root = document.getElementById(this.root);
		if (!root) return;
		const showBlend = this.state.terminalValueMethod === 'blended';
		root.querySelectorAll('.tv-blend-weights').forEach(el => {
			el.style.display = showBlend ? '' : 'none';
		});
	}

	// ══════════════════════════════════════════════
	//  Shared Helper methods
	// ══════════════════════════════════════════════

	_isCrossCurrency() {
		const s = this.state;
		if (!s.reportCurrency || !s.priceCurrency) return false;
		return s.reportCurrency.toLowerCase() !== s.priceCurrency.toLowerCase();
	}

	_lastForecastPeriod() {
		const periods = this.state.periods;
		if (!periods) return null;
		for (let i = periods.length - 1; i >= 0; i--) {
			if (!periods[i].isActual) return periods[i];
		}
		return null;
	}

	_baseRevenue() {
		const periods = this.state.periods;
		if (!periods) return 0;
		for (const p of periods) {
			if (p.isActual && p.revenue != null) return p.revenue;
		}
		return 0;
	}

	_findClosestIndex(arr, target) {
		if (!arr || arr.length === 0) return 0;
		let closest = 0;
		let minDiff = Math.abs(arr[0] - target);
		for (let i = 1; i < arr.length; i++) {
			const diff = Math.abs(arr[i] - target);
			if (diff < minDiff) { minDiff = diff; closest = i; }
		}
		return closest;
	}

	_getSensitivityBaseColValue(st) {
		if (st.colFormat === 'pct') return this.state.terminalGrowthRate;
		return this.state.exitMultiple;
	}

	_formatAxisValue(value, format) {
		switch (format) {
			case 'pct': return (value * 100).toFixed(1) + '%';
			case 'multiple': return value.toFixed(1) + '×';
			default: return value.toFixed(1);
		}
	}

	_formatDate(dateVal) {
		if (!dateVal) return '';
		const d = new Date(dateVal);
		const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
			'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
		return `${months[d.getMonth()]} ${String(d.getDate()).padStart(2, '0')}, ${d.getFullYear()}`;
	}

	/** Blend TV based on selected method */
	_blendTv(s, tvPerp, tvExit) {
		switch (s.terminalValueMethod) {
			case 'perpetuity_growth': return tvPerp;
			case 'exit_multiple': return tvExit;
			case 'blended':
			default:
				return tvPerp * s.blendWeightPerpetuity + tvExit * s.blendWeightExit;
		}
	}

	/**
	 * Build projections from base revenue given growth, margin, and ratios.
	 * Used by sensitivity recalculations and reverse DCF solvers.
	 * Returns { sumPv, lastUfcf, lastEbitda }.
	 */
	_buildProjections(baseRevenue, growth, margin, wacc, opts = {}) {
		const daRatio = opts.daRatio || 0.05;
		const capexRatio = opts.capexRatio || 0.08;
		const nwcRatio = opts.nwcRatio || 0.01;
		const sbcRatio = opts.sbcRatio || 0;
		const taxRate = opts.taxRate || 0.21;
		const projYears = this.state.projectionYears;

		let prevRev = baseRevenue, sumPv = 0, lastUfcf = 0, lastEbitda = 0;

		for (let i = 1; i <= projYears; i++) {
			const rev = prevRev * (1 + growth);
			const ebitda = rev * margin;
			const da = rev * daRatio;
			const ebit = ebitda - da;
			const nopat = ebit * (1 - taxRate);
			const capex = rev * capexRatio;
			const nwc = rev * nwcRatio;
			const sbc = rev * sbcRatio;
			const ufcf = nopat + da - capex - nwc + sbc;

			const period = this.state.midYearConvention ? i - 0.5 : i;
			const df = Math.pow(1 + wacc, -period);
			sumPv += ufcf * df;

			lastUfcf = ufcf;
			lastEbitda = ebitda;
			prevRev = rev;
		}

		return { sumPv, lastUfcf, lastEbitda };
	}

	/**
	 * Calculate PV of Terminal Value from last-period values.
	 * Shared between DCF sensitivity and RDCF solvers.
	 */
	_calcPvTv(wacc, lastUfcf, lastEbitda, tgr, projYears) {
		const s = this.state;
		const denom = wacc - tgr;
		const tvPerp = denom > 0 ? (lastUfcf * (1 + tgr)) / denom : 0;
		const tvExit = lastEbitda * s.exitMultiple;
		const tvUndiscounted = this._blendTv(s, tvPerp, tvExit);
		return tvUndiscounted * Math.pow(1 + wacc, -projYears);
	}

	/**
	 * Generic bisection solver. Returns the root or null if no convergence.
	 */
	_bisect(lo, hi, fn) {
		const maxIter = 100;
		const tol = 1e-7;

		let fLo = fn(lo);
		let fHi = fn(hi);

		if (fLo * fHi > 0) return null;

		for (let iter = 0; iter < maxIter; iter++) {
			const mid = (lo + hi) / 2;
			const fMid = fn(mid);

			if (Math.abs(fMid) < tol * Math.abs(fn(lo) + fn(hi) + 1) || Math.abs(hi - lo) < tol) {
				return mid;
			}

			if (fMid * fLo < 0) {
				hi = mid;
				fHi = fMid;
			} else {
				lo = mid;
				fLo = fMid;
			}
		}
		return null;
	}
}